import React, { Component } from 'react';
import { Drawer, Button, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import colors from 'utils/colors';
import { DialogWrapper } from 'components/DialogWrapper';
import QuestionnaireAnswers from 'containers/Manage-Questionnaire/questionnaire/QuestionnaireAnswers';
import {
  DrawerHeader,
  FlexRow,
  BlueTextPointer,
  PlainButton,
  BorderWrapper,
} from 'components/styled-components';
import filter from 'lodash/filter';
import cloneDeep from 'lodash/cloneDeep';
import FormTextAreaGroup from 'components/forms/components/form-text-area-group';
import ErrorWrapper from 'components/forms/components/error-wrapper';
import { createQuestionnaireQuestion } from 'api/questionnaire';
import { AppToaster } from 'components/AppToaster';
import { QuestionDetails } from 'components/forms/questionnaire-management/question-details';
import {
  HeaderControls,
  ScrollWrapper,
} from 'containers/Manage-Questionnaire/styled-components';

const initialState = {
  editMade: false,
  removeQuestion: false,
  closeWithoutSaving: false,
  questionError: false,
  question: '',
  weight: 1,
  displayCategory: undefined,
  questionType: undefined,
  controlID: '',
  defaultQuestion: false,
  addingAnswer: false,
  answers: [],
  editingAnswer: false,
  answerToEdit: {},
};

class QuestionnaireAddQuestionDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = cloneDeep(initialState);
  }

  handleCloseDrawer = () => {
    if (this.state.editMade) {
      this.toggleCloseWithoutSaving();
    } else {
      this.resetComponent();
      this.props.closeDrawer();
    }
  };

  resetComponent = () => this.setState(cloneDeep(initialState));

  handlePageChange = nextPage => {
    this.props.closeDrawer();
    this.props.setPrescreenIndex(nextPage);
  };

  handleQuestionChange = e => {
    const question = e.target.value;
    this.setState({ question });
    this.validateQuestion(question);
    this.setState({ editMade: true });
  };

  handleAnswerValueChange = (answer, index) => {
    const { answers } = this.state;
    if (answers.length === 0) {
      answers.push(answer);
    } else {
      answers[index] = answer;
      this.setState({ answers });
    }
  };

  handleCreateAnswer = answer => {
    const { answers } = this.state;
    answers.push(answer);
    this.setState({ answers });
  };

  validateQuestion = question => {
    if (question.length > 0) {
      this.setState({ questionError: false });
    } else {
      this.setState({ questionError: true });
    }
  };

  handleDisplayCategoryChange = displayCategory => {
    this.setState({ displayCategory });
    this.setState({ editMade: true });
  };

  handleQuestionTypeChange = questionType => {
    this.setState({ questionType });
    this.setState({ editMade: true });
  };

  handleControlIDChange = e => {
    const controlID = e.target.value;
    this.setState({ controlID });
    this.validateControlID(controlID);
    this.setState({ editMade: true });
  };

  handleWeightChange = weight => {
    this.setState({ weight });
    this.validateWeightValue(weight);
    this.setState({ editMade: true });
  };

  handleDefaultChange = defaultQuestion => {
    this.setState({ defaultQuestion });
    this.setState({ editMade: true });
  };

  validateControlID = controlID => {
    if (controlID !== '') {
      this.setState({ controlError: false });
    } else {
      this.setState({ controlError: true });
    }
  };

  validateWeightValue = weight => {
    if (weight !== '') {
      this.setState({ weightError: false });
    } else {
      this.setState({ weightError: true });
    }
  };

  handleDismissRemoveQuestion = () => this.setState({ removeQuestion: false });
  toggleCloseWithoutSaving = () =>
    this.setState({ closeWithoutSaving: !this.state.closeWithoutSaving });

  handleCreateQuestion = () => {
    const {
      question,
      weight,
      controlID,
      displayCategory,
      questionType,
      answers,
      defaultQuestion,
    } = this.state;
    createQuestionnaireQuestion({
      question,
      weight,
      controlID,
      displayCategory,
      questionType,
      answers,
      defaultQuestion,
    })
      .then(res => {
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        });
        this.props.fetchQuestionnaireQuestions();
        this.resetComponent();
        this.props.closeDrawer();
      })
      .catch(err => {
        AppToaster.show({
          message: err.response.data.message,
          intent: Intent.DANGER,
        });
      });
  };

  handleArchiveAnswer = index => {
    const answers = filter(
      cloneDeep(this.state.answers),
      (a, i) => i !== index
    );
    this.setState({ answers });
  };

  render() {
    const {
      question,
      answers,
      questionType,
      controlID,
      displayCategory,
      weight,
      defaultQuestion,
      controlError,
      weightError,
    } = this.state;
    const { isOpen } = this.props;

    return (
      <Drawer isOpen={isOpen} onClose={this.handleCloseDrawer}>
        <DrawerHeader>
          Add Question
          {this.state.editMade ? (
            <FlexRow fontSize="0.7rem" width="15rem">
              <BlueTextPointer onClick={this.toggleCloseWithoutSaving}>
                DISCARD
              </BlueTextPointer>
              <PlainButton
                onClick={this.handleCreateQuestion}
                disabled={
                  this.state.questionError ||
                  this.state.answers.length === 0 ||
                  !displayCategory ||
                  !questionType ||
                  controlID === ''
                }
              >
                SAVE CHANGES
              </PlainButton>
            </FlexRow>
          ) : (
            <HeaderControls>
              <Button
                icon={IconNames.CROSS}
                onClick={this.handleCloseDrawer}
                minimal
              />
            </HeaderControls>
          )}
        </DrawerHeader>
        <ScrollWrapper>
          {this.state.questionError && (
            <ErrorWrapper>The question field cannot be blank.</ErrorWrapper>
          )}
          <FormTextAreaGroup
            label="Text"
            requiredLabel={false}
            name="question"
            fill={true}
            value={question}
            handleChange={this.handleQuestionChange}
            style={{ background: colors.formBackground }}
          />
          <BorderWrapper>
            <QuestionnaireAnswers
              answers={answers}
              openParent={this.props.openDrawer}
              isParentOpen={this.props.isOpen}
              question={question}
              newQuestion={true}
              fetchQuestionnaireQuestions={
                this.props.fetchQuestionnaireQuestions
              }
              handleAnswerValueChange={this.handleAnswerValueChange}
              handleCreateAnswer={this.handleCreateAnswer}
              categories={this.props.categories}
              archiveLocalAnswer={this.handleArchiveAnswer}
            />
          </BorderWrapper>
          <QuestionDetails
            weight={weight}
            category={displayCategory}
            questionType={questionType}
            controlID={controlID}
            defaultQuestion={defaultQuestion}
            handleControlIDChange={this.handleControlIDChange}
            handleWeightChange={this.handleWeightChange}
            handleQuestionTypeChange={this.handleQuestionTypeChange}
            handleCategoryChange={this.handleDisplayCategoryChange}
            handleDefaultChange={this.handleDefaultChange}
            controlError={controlError}
            weightError={weightError}
            {...this.props}
          />

          <DialogWrapper
            isOpen={this.state.closeWithoutSaving}
            onClose={this.toggleCloseWithoutSaving}
            title="You have unsaved changes on this page"
            body="If you leave this page, all unsaved changes will be lost. Are you sure you want to leave this page?"
            confirm="LEAVE PAGE"
            action={() => {
              this.setState(
                { editMade: false, closeWithoutSaving: false },
                this.handleCloseDrawer
              );
            }}
          />
        </ScrollWrapper>
      </Drawer>
    );
  }
}

export default QuestionnaireAddQuestionDrawer;
